import React, { useEffect, useState } from 'react';
import { Form, Schema, Stack, Col, Grid, Row, IconButton, Divider, FormInstance, InputGroup, SelectPicker} from 'rsuite';
import { useNavigate } from 'react-router-dom';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import PageContent from '@/components/PageContent';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import ErrorMessage from '@/components/ErrorMessage';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import ImageUploader from '@/components/ImageUploader';
import { getCitiesLookup, getCity } from '@/services/city.service';
import { RunEvent, RunEventStatusList, RunEventTypeList, UpsertRunEvent } from '@/models/run-event.model';
import { submitRunEvent, updateRunEvent } from '@/services/run-event.service';
import Textarea from '@/components/Textarea';
import { City } from '@/models/city.model';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import FormControl from 'rsuite/esm/FormControl';
import { Icon } from '@rsuite/icons';
import { MdContentCopy, MdLink } from 'react-icons/md';
import { getRunEventKeysLookup } from '@/services/run-event-key.service';
import EventLinksEditor from '@/components/EventLinksEditor';
import { cleanEventLinks } from '@/models/event-link.model';

const insertSchema = Schema.Model<UpsertRunEvent>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  description: Schema.Types.StringType().isRequired('This field is required.'),
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  runEventKeyId: Schema.Types.NumberType(),
  website: Schema.Types.StringType(),
  imageUrl: Schema.Types.StringType(),
  registrationDate: Schema.Types.DateType(),
  type: Schema.Types.NumberType().isRequired('This field is required.'),
  status: Schema.Types.NumberType(),
  isMajor: Schema.Types.BooleanType().isRequired('This field is required.'),
});
const updateSchema = Schema.Model<UpsertRunEvent>({
  id: Schema.Types.NumberType(),
  name: Schema.Types.StringType().isRequired('This field is required.'),
  description: Schema.Types.StringType().isRequired('This field is required.'),
  cityId: Schema.Types.NumberType().isRequired('This field is required.'),
  runEventKeyId: Schema.Types.NumberType(),
  website: Schema.Types.StringType(),
  imageUrl: Schema.Types.StringType(),
  registrationDate: Schema.Types.DateType(),
  type: Schema.Types.NumberType().isRequired('This field is required.'),
  status: Schema.Types.NumberType().isRequired('This field is required.'),
  isMajor: Schema.Types.BooleanType().isRequired('This field is required.'),
});


const emptyModel: UpsertRunEvent = {
  id: undefined,
  name: undefined,
  description: undefined,
  cityId: undefined,
  runEventKeyId: undefined,
  imageUrl: undefined,
  website: undefined,
  links: [],
  registrationDate: undefined,
  type: undefined,
  status: undefined,
  isMajor: false
};

interface RunEventFormProps{
  formObject?: RunEvent;
  pageState: PageState
};

const RunEventForm: React.FC<RunEventFormProps> = ({formObject, pageState}) => {
  const [formValue, setFormValue] = useState<UpsertRunEvent>(emptyModel);
  const [formError, setFormError] = useState<Record<string, any>>({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState< City | undefined>(undefined);
  
  const nav = useNavigate();
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  useEffect(() => {
    if(pageState == PageState.New){
      setFormValue({...emptyModel, });
      return;
    }

    if (formObject) {
      setFormValue({...formObject, links: formObject.links ?? []});
      updateSelectedCity(formObject.cityId);

    } else {
      setFormValue(emptyModel);
    }
  }, [formObject]);

  const updateSelectedCity = async(cityId: number|undefined) => {
    if(!cityId){
      setSelectedCity(undefined);
      return;
    }

    const city = (await getCity(cityId)).result;
    setSelectedCity(city);
    setFormValue(pre => ({...pre, countryId: city?.country?.id}));
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      console.log("errors:", formError);
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = pageState == PageState.Edit ? 
        await updateRunEvent({...formValue, links: cleanEventLinks(formValue.links)}) : 
        await submitRunEvent({...formValue, links: cleanEventLinks(formValue.links)});
      if (result.success) {
        nav('/run-events');
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    }finally{
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };
  const handleCityChange = (value) => {
    setFormValue(pre => ({ ...pre, cityId: value}));
    updateSelectedCity(value);
  };
  return (
    <PageContent>
      <Form name='run-event-form' fluid 
        ref={formRef}
        onChange={handleFormChange}
        onCheck={setFormError}
        formValue={formValue}
        model={pageState == PageState.New ? insertSchema : updateSchema}
        disabled={loading}
      >
        <Grid w='100%' fluid>
          <Row>
            <Col span={{xs:24, xl:8}} style={{textAlign: 'center'}}>
              <Form.Label>Image</Form.Label>              
              <Form.Control
                accepter={ImageUploader}
                name="imageUrl"
                height="400px"
                url={formValue.imageUrl}
                objectStorageCategory={ObjectStorageFileCategory.RunEvent}
                onChange={url => setFormValue(pre => ({...pre, imageUrl: url}))}                
              />            
            </Col> 
            <Col span={{xs:24, xl:16}} >
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Name </Form.Label>
                    <Form.Control name="name"/> 
                  </Form.Group>
                </Col>               
              </Row>
              <Row>
                <Col span={{xs:24, lg:12}} >
                  <Form.Group>
                    <Form.Label>Type</Form.Label>
                    <Form.Control name="type" accepter={SelectPicker} block
                      data={RunEventTypeList} 
                      searchable={false}
                      placeholder="Select a type"
                      disabled={pageState == PageState.Edit}
                    /> 
                  </Form.Group>
                </Col>                
                <Col span={{xs:24, lg:12}} >
                  <Form.Group controlId='cityId'>
                    <Form.Label>City</Form.Label>
                    <Form.Control name="cityId" accepter={FetchingSelect} block
                      fetchData={(text, page) => getCitiesLookup(text, undefined, page)} 
                      placeholder="Select a city"
                      onChange={handleCityChange}
                    /> 
                  </Form.Group>
                </Col>                
              </Row>
              
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group controlId='description'>
                    <Form.Label>description</Form.Label>
                    <Form.Control name="description" accepter={Textarea} rows={4}/> 
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, lg:12}} >
                  <Form.Group>
                    <Form.Label>Website</Form.Label>
                    <InputGroup inside>
                      <InputGroup.Button onClick={() => formValue?.website && window.open(formValue?.website, '_blank') }>
                        <Icon as={MdLink} />
                      </InputGroup.Button>
                      <Form.Control name="website" />
                      <InputGroup.Button onClick={() => navigator.clipboard.writeText(formValue?.website || '')}>
                        <Icon as={MdContentCopy} />
                      </InputGroup.Button>
                    </InputGroup>                     
                  </Form.Group>
                </Col>
                <Col span={{xs:24, lg:12}}>
                  <Form.Group>
                    <Form.Label>Key</Form.Label>
                    <Form.Control name="runEventKeyId" accepter={FetchingSelect} block
                      fetchData={(text, page) =>getRunEventKeysLookup(text, formValue.cityId, page)} 
                      placeholder="Select a key"
                      reFetchOnOpen = {true}
                    /> 
                  </Form.Group>                
                </Col>
              </Row>              
              <Row>
                <Col span={{xs:24}}>
                  <Form.Group>
                    <Form.Label>Links</Form.Label>
                    <EventLinksEditor value={formValue.links} disabled={loading}
                      onChange={links => setFormValue(pre => ({...pre, links}))} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col span={{xs:24, lg:12}} >
                  <Form.Group>
                    <Form.Label>Registration Date</Form.Label>
                    <FormControl accepter={ZonedDatePicker} block
                      name="registrationDate" 
                      value={formValue.registrationDate ?? null}
                      onChange={dt => setFormValue(pre => ({...pre, registrationDate: dt ?? undefined}))}
                      zoneIanaId={selectedCity?.ianaTimeZoneId}
                    />                  
                  </Form.Group>                   
                </Col>
                <Col span={{xs:24, lg:12}} >
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Control name="status" accepter={SelectPicker} block
                      data={RunEventStatusList} 
                      searchable={false}
                      placeholder="Select a status"
                      disabled={pageState == PageState.New}
                    /> 
                  </Form.Group>
                </Col>
              </Row>              
            </Col>
          </Row>          
        </Grid>
      </Form>
      <Divider />
      {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

      <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
          Back
        </IconButton>
        <IconButton
          icon={<PageNextIcon />}
          placement="right"
          appearance="primary"
          onClick={handleSubmit}
          loading={loading}
        >
          Save
        </IconButton>
      </Stack>
    </PageContent>
  );
};

export default RunEventForm;

